import IRootScopeService = angular.IRootScopeService;

export interface INotification {
  type?: string;
  title?: string;
  description?: string;
  buttons?: string;
  unique?: boolean;
  timeout?: number;
  count?: number;
}

export interface INotificationService {
  TYPE: { ERROR: string; SUCCESS: string; INFO: string };
  EVENTS: { DISMISS_ALL: string };
  dismiss(index: number): void;
  dismissAll(): void;
  getCurrentMessages(): INotification[];
  indexOfMessage(msg: Partial<INotification>): number;
  addCurrentMessage(message: INotification): void;
  addFlashMessage(msg: INotification): void;
  addFlashSuccess(msg: Partial<INotification>): void;
  addFlashInfo(msg: Partial<INotification>): void;
  addFlashError(msg: Partial<INotification>): void;
  addError(msg: Partial<INotification>): void;
  addInfo(msg: Partial<INotification>): void;
  addSuccess(msg: Partial<INotification>): void;
}

export class NotificationService implements INotificationService {
  public TYPE = {
    ERROR: 'error',
    SUCCESS: 'success',
    INFO: 'info'
  };

  public EVENTS = {
    DISMISS_ALL: 'dismiss_all'
  };

  private defaults: INotification = {
    type: this.TYPE.INFO,
    title: '',
    description: '',
    buttons: '',
    unique: false,
    timeout: 0
  };

  private identity = ['type', 'title', 'description', 'buttons', 'timeout'];

  private store = {
    current: [] as INotification[],
    flash: [] as INotification[],
    global: [] as INotification[]
  };

  constructor(private rootScope: IRootScopeService) {
    rootScope.$on('$routeChangeSuccess', () => {
      while (this.store.flash.length > 0) {
        this.addCurrentMessage(this.store.flash.shift());
      }
    });
  }

  dismiss(index: number): void {
    if (this.store.current[index]) {
      this.store.current.splice(index, 1);
    }
  }

  dismissAll(): void {
    this.rootScope.$broadcast(this.EVENTS.DISMISS_ALL);
  }

  getCurrentMessages(): INotification[] {
    return this.store.current;
  }

  indexOfMessage(msg: Partial<INotification>): number {
    let messageIndex = -1;

    const getClean = (m: Partial<INotification>) => _.pick(m, this.identity);

    const keys = _.filter(Object.keys(getClean(msg)), (key) => msg[key] !== undefined);

    _.each(this.store.current, (message, index) => {
      if (_.every(keys, (key) => getClean(message)[key] === getClean(msg)[key])) {
        messageIndex = index;
      }
    });

    return messageIndex;
  }

  addCurrentMessage(message: INotification): void {
    _.defaults(message, this.defaults);
    const index = this.indexOfMessage(message);
    if (index >= 0) {
      this.store.current[index].count++;
    } else {
      this.store.current.push(message);
    }
  }

  addFlashMessage(msg: INotification): void {
    this.store.flash.push(msg);
  }

  addFlashSuccess(msg: Partial<INotification>): void {
    const message = _.extend(msg, { type: this.TYPE.SUCCESS }) as INotification;
    this.addFlashMessage(message);
  }

  addFlashInfo(msg: Partial<INotification>): void {
    const message = _.extend(msg, { type: this.TYPE.INFO }) as INotification;
    this.addFlashMessage(message);
  }

  addFlashError(msg: Partial<INotification>): void {
    const message = _.extend(msg, { type: this.TYPE.ERROR }) as INotification;
    this.addFlashMessage(message);
  }

  addError(msg: Partial<INotification>): void {
    const message = _.extend(msg, { type: this.TYPE.ERROR }) as INotification;
    this.addCurrentMessage(message);
  }

  addInfo(msg: Partial<INotification>): void {
    const message = _.extend(msg, { type: this.TYPE.INFO }) as INotification;
    this.addCurrentMessage(message);
  }

  addSuccess(msg: Partial<INotification>): void {
    const message = _.extend(msg, { type: this.TYPE.SUCCESS }) as INotification;
    this.addCurrentMessage(message);
  }
}

// Note: original notification.js injected $timeout but never used it; omitted here.
frontendApp.factory('NotificationService', ['$rootScope',
  function($rootScope: IRootScopeService) {
    return new NotificationService($rootScope);
  }
]);
